import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ClipboardList, Search, Plus, Star, FileText, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EvaluationsAdminPage = () => {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API}/evaluations/all`, { credentials: 'include' });
      if (res.ok) setEvaluations(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const filtered = evaluations.filter(e =>
    e.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.session_title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="evaluations-admin-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Évaluations</h1>
            <p className="text-slate-500">Gérez les évaluations et questionnaires</p>
          </div>
          <Button className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="add-evaluation-btn">
            <Plus className="h-4 w-4 mr-2" /> Nouvelle évaluation
          </Button>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <Card><CardContent className="pt-6"><p className="text-sm text-slate-500">Total</p><p className="text-2xl font-bold text-slate-900">{evaluations.length}</p></CardContent></Card>
          <Card><CardContent className="pt-6"><p className="text-sm text-slate-500">Satisfaction</p><p className="text-2xl font-bold text-teal-600">{evaluations.filter(e => e.type === 'satisfaction').length}</p></CardContent></Card>
          <Card><CardContent className="pt-6"><p className="text-sm text-slate-500">Acquis</p><p className="text-2xl font-bold text-blue-600">{evaluations.filter(e => e.type === 'knowledge').length}</p></CardContent></Card>
          <Card><CardContent className="pt-6"><p className="text-sm text-slate-500">Réponses</p><p className="text-2xl font-bold text-emerald-600">{evaluations.filter(e => e.status === 'completed').length}</p></CardContent></Card>
        </div>

        <Card>
          <CardHeader>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Rechercher..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" />
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Évaluation</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Session</TableHead>
                  <TableHead>Questions</TableHead>
                  <TableHead>Échéance</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(ev => (
                  <TableRow key={ev.evaluation_id} data-testid={`eval-admin-row-${ev.evaluation_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                          {ev.type === 'satisfaction' ? <Star className="h-5 w-5 text-teal-600" /> : <FileText className="h-5 w-5 text-teal-600" />}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{ev.title}</p>
                          <p className="text-xs text-slate-500">{ev.description?.substring(0, 60)}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={ev.type === 'satisfaction' ? 'bg-teal-100 text-teal-700' : 'bg-blue-100 text-blue-700'}>
                        {ev.type === 'satisfaction' ? 'Satisfaction' : 'Acquis'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{ev.session_title}</TableCell>
                    <TableCell className="text-sm">{ev.questions?.length || 0} questions</TableCell>
                    <TableCell className="text-sm">{ev.due_date ? new Date(ev.due_date).toLocaleDateString('fr-FR') : '-'}</TableCell>
                    <TableCell>
                      <Badge className={ev.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-teal-100 text-teal-700'}>
                        {ev.status === 'completed' ? 'Complétée' : 'En attente'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Aucune évaluation trouvée</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default EvaluationsAdminPage;
